import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { MessageSquarePlus } from "lucide-react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { useDebounce } from "@/hooks/useDebounce"
import { useMediaQuery } from "@/hooks/use-media-query"
import { useReadOnly } from "@/lib/read-only"
import { outline, parsePrompt, type Highlight } from "@/lib/prompt-markdown"
import { selectionRange, trimRange, type SourceRange } from "@/lib/prompt-selection"
import { downloadText, exportMarkdown } from "@/lib/prompt-export"
import { offsetMap } from "@/lib/prompt-offsets"
import { queriesToResync } from "@/lib/prompt-resync"
import {
  auditErrorMessage,
  createComment,
  deleteComment,
  getPrompt,
  getPromptIndex,
  getThreads,
  promptAuditKeys,
  replyToThread,
  searchPrompts,
  setThreadResolved,
  type PromptDetail,
  type Thread,
} from "@/lib/prompt-audit-api"
import { AuditOverview } from "@/components/prompt-audit/audit-overview"
import { CommentMargin, type MarginEntry } from "@/components/prompt-audit/comment-margin"
import { CommentComposer, ThreadCard } from "@/components/prompt-audit/comment-thread"
import { MemberPreview } from "@/components/prompt-audit/member-preview"
import { OVERVIEW_ID, PromptNav } from "@/components/prompt-audit/prompt-nav"
import { PromptHeader } from "@/components/prompt-audit/prompt-header"
import { PromptReader, type ReaderMode } from "@/components/prompt-audit/prompt-reader"

const PREVIEW_IDS = new Set(["surface.primary.system", "template.primary.turn_context"])
const FLASH_MS = 1600
const JUMP_MARGIN = 24
const PENDING_ID = "__pending"
const WIDE_QUERY = "(min-width: 1280px)"

type Pending = SourceRange & { quote: string; top: number; composing: boolean }

function scrollerOf(element: HTMLElement | null): HTMLElement {
  for (let node = element; node; node = node.parentElement) {
    const overflow = getComputedStyle(node).overflowY
    if ((overflow === "auto" || overflow === "scroll") && node.scrollHeight > node.clientHeight) return node
  }
  return (document.scrollingElement as HTMLElement | null) ?? document.documentElement
}

function useAnchorTops(container: HTMLElement | null, ids: string[], version: string) {
  const [tops, setTops] = useState<Record<string, number>>({})
  const key = ids.join("|")
  useLayoutEffect(() => {
    if (!container) return
    const measure = () => {
      const base = container.getBoundingClientRect().top
      const next: Record<string, number> = {}
      for (const id of key ? key.split("|") : []) {
        const mark = container.querySelector(`mark[data-thread="${CSS.escape(id)}"]`)
        if (mark) next[id] = mark.getBoundingClientRect().top - base
      }
      setTops(next)
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(container)
    return () => observer.disconnect()
  }, [container, key, version])
  return tops
}

export function PromptsPage() {
  const [params, setParams] = useSearchParams()
  const selected = params.get("p") ?? OVERVIEW_ID
  const { readOnly } = useReadOnly()
  const queryClient = useQueryClient()
  const [mode, setMode] = useState<ReaderMode>("rendered")
  const [showResolved, setShowResolved] = useState(false)
  const [activeThread, setActiveThread] = useState<string | null>(null)
  const [pending, setPending] = useState<Pending | null>(null)
  const [query, setQuery] = useState("")
  const debounced = useDebounce(query.trim(), 250)
  const scrollRef = useRef<HTMLDivElement>(null)
  const [reader, setReader] = useState<HTMLDivElement | null>(null)
  const [readerOffset, setReaderOffset] = useState(0)
  const wide = useMediaQuery(WIDE_QUERY)

  const index = useQuery({ queryKey: promptAuditKeys.index, queryFn: getPromptIndex })
  const isEntry = selected !== OVERVIEW_ID
  const detail = useQuery({ queryKey: promptAuditKeys.detail(selected), queryFn: () => getPrompt(selected), enabled: isEntry })
  const commentsAvailable = index.data?.comments_available ?? false
  const threads = useQuery({
    queryKey: promptAuditKeys.threads(selected),
    queryFn: () => getThreads(selected),
    enabled: isEntry && commentsAvailable,
  })
  const search = useQuery({
    queryKey: promptAuditKeys.search(debounced),
    queryFn: () => searchPrompts(debounced),
    enabled: debounced.length >= 2,
  })

  const text = detail.data?.text ?? ""
  const offsets = useMemo(() => offsetMap(text), [text])
  const current: PromptDetail | undefined = useMemo(() => {
    if (!detail.data) return undefined
    const local = (part: PromptDetail["parts"][number]) => ({
      ...part, start: offsets.toUtf16(part.start), end: offsets.toUtf16(part.end),
    })
    return { ...detail.data, parts: detail.data.parts.map(local), contains: detail.data.contains.map(local) }
  }, [detail.data, offsets])
  const threadsMatchText = !!threads.data && threads.data.sha256 === detail.data?.sha256
  const blocks = useMemo(() => parsePrompt(text), [text])
  const outlineItems = useMemo(() => outline(text, blocks), [text, blocks])
  const summaries = useMemo(
    () => Object.fromEntries((index.data?.entries ?? []).map((entry) => [entry.id, entry])),
    [index.data],
  )
  const allThreads = useMemo(() => {
    if (!threads.data || !threadsMatchText) return []
    return threads.data.threads.map((thread) => thread.anchor.start === null ? thread : {
      ...thread,
      anchor: { ...thread.anchor, start: offsets.toUtf16(thread.anchor.start), end: offsets.toUtf16(thread.anchor.end!) },
    })
  }, [threads.data, threadsMatchText, offsets])
  const visibleThreads = useMemo(
    () => allThreads.filter((thread) => showResolved || !thread.resolved_at),
    [allThreads, showResolved],
  )
  const anchored = useMemo(() => visibleThreads.filter((thread) => thread.anchor.start !== null), [visibleThreads])
  const unanchored = useMemo(() => visibleThreads.filter((thread) => thread.anchor.start === null), [visibleThreads])
  const highlights: Highlight[] = useMemo(
    () => anchored.map((thread) => ({ id: thread.id, start: thread.anchor.start!, end: thread.anchor.end! })),
    [anchored],
  )
  const canComment = commentsAvailable && !readOnly
  const tops = useAnchorTops(reader, anchored.map((thread) => thread.id), `${mode}:${text.length}:${highlights.length}`)
  const unplaced = useMemo(() => anchored.filter((thread) => tops[thread.id] === undefined), [anchored, tops])
  const exportReady = !commentsAvailable || (threads.isSuccess && threadsMatchText)

  const resyncing = detail.isFetching || threads.isFetching
  useEffect(() => {
    for (const query of queriesToResync(detail.data?.sha256, threads.data?.sha256, resyncing)) {
      queryClient.invalidateQueries({ queryKey: promptAuditKeys[query](selected) })
    }
  }, [threads.data, detail.data, resyncing, queryClient, selected])

  useEffect(() => {
    if (!canComment) setPending(null)
  }, [canComment])

  useLayoutEffect(() => {
    if (!reader) return
    const article = reader.closest("article")
    const update = () => setReaderOffset(article ? reader.getBoundingClientRect().top - article.getBoundingClientRect().top : 0)
    update()
    const observer = new ResizeObserver(update)
    observer.observe(reader.parentElement ?? reader)
    return () => observer.disconnect()
  }, [reader, detail.data])

  useEffect(() => {
    setPending(null)
    setActiveThread(null)
    scrollerOf(scrollRef.current).scrollTo({ top: 0 })
  }, [selected])

  useEffect(() => {
    if (!reader || !text || debounced.length < 2) return
    const at = text.toLowerCase().indexOf(debounced.toLowerCase())
    if (at >= 0) jumpRef.current?.(at)
  }, [reader, text, debounced])

  const select = useCallback((id: string) => {
    setParams(id === OVERVIEW_ID ? {} : { p: id })
  }, [setParams])

  const jump = useCallback((offset: number) => {
    if (!reader) return
    const targets = [...reader.querySelectorAll<HTMLElement>("[data-block-start]")]
    const target = targets.filter((element) => Number(element.dataset.blockStart) <= offset).pop() ?? targets[0]
    if (!target) return
    const scroller = scrollerOf(scrollRef.current)
    const origin = scroller === document.scrollingElement ? 0 : scroller.getBoundingClientRect().top
    const top = target.getBoundingClientRect().top - origin + scroller.scrollTop - JUMP_MARGIN
    scroller.scrollTo({ top, behavior: "smooth" })
    target.classList.add("bg-primary/10")
    setTimeout(() => target.classList.remove("bg-primary/10"), FLASH_MS)
  }, [reader])

  const jumpRef = useRef(jump)
  jumpRef.current = jump

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: promptAuditKeys.detail(selected) })
    queryClient.invalidateQueries({ queryKey: promptAuditKeys.threads(selected) })
    queryClient.invalidateQueries({ queryKey: promptAuditKeys.index })
  }
  const onError = (error: unknown) => toast.error(auditErrorMessage(error))
  const create = useMutation({
    mutationFn: ({ body, at }: { body: string; at: Pending }) => createComment(selected, {
      content_sha256: detail.data!.sha256,
      start: offsets.toCodePoint(at.start),
      end: offsets.toCodePoint(at.end),
      quote: at.quote,
      body,
    }),
    onSuccess: (thread) => {
      setPending(null)
      setActiveThread(thread.id)
      window.getSelection()?.removeAllRanges()
      refresh()
    },
    onError,
  })
  const reply = useMutation({
    mutationFn: ({ id, body }: { id: string; body: string }) => replyToThread(id, body),
    onSuccess: refresh,
    onError,
  })
  const resolve = useMutation({
    mutationFn: ({ id, resolved }: { id: string; resolved: boolean }) => setThreadResolved(id, resolved),
    onSuccess: (_data, variables) => {
      if (variables.resolved && !showResolved) setActiveThread(null)
      refresh()
    },
    onError,
  })
  const remove = useMutation({ mutationFn: deleteComment, onSuccess: refresh, onError })
  const busy = create.isPending || reply.isPending || resolve.isPending || remove.isPending

  const captureSelection = useCallback(() => {
    if (!reader || !canComment || !detail.data) return
    const selection = window.getSelection()
    const raw = selectionRange(reader, selection)
    const range = raw && trimRange(detail.data.text, raw)
    if (!range || !selection) {
      setPending((current) => (current?.composing ? current : null))
      return
    }
    const top = selection.getRangeAt(0).getBoundingClientRect().top - reader.getBoundingClientRect().top
    setPending({ ...range, quote: detail.data.text.slice(range.start, range.end), top, composing: false })
    setActiveThread(null)
  }, [reader, canComment, detail.data])

  const onHighlightClick = useCallback((id: string) => {
    setPending(null)
    setActiveThread(id)
  }, [])

  const threadCard = (thread: Thread) => (
    <ThreadCard
      key={thread.id}
      thread={thread}
      active={activeThread === thread.id}
      canWrite={canComment}
      busy={busy}
      onActivate={() => setActiveThread(thread.id)}
      onReply={async (body) => {
        try {
          await reply.mutateAsync({ id: thread.id, body })
          return true
        } catch {
          return false
        }
      }}
      onResolve={(resolved) => resolve.mutate({ id: thread.id, resolved })}
      onDelete={(id) => remove.mutate(id)}
    />
  )

  const marginEntries: MarginEntry[] = anchored
    .filter((thread) => tops[thread.id] !== undefined)
    .map((thread) => ({ id: thread.id, top: tops[thread.id], node: threadCard(thread) }))
  if (pending) {
    marginEntries.push({
      id: PENDING_ID,
      top: pending.top,
      node: pending.composing ? (
        <CommentComposer
          quote={pending.quote}
          busy={create.isPending}
          onCancel={() => setPending(null)}
          onSubmit={async (body) => {
            if (!canComment) return false
            try {
              await create.mutateAsync({ body, at: pending })
              return true
            } catch {
              return false
            }
          }}
        />
      ) : (
        <Button size="sm" variant="outline" className="shadow-sm" onClick={() => setPending({ ...pending, composing: true })}>
          <MessageSquarePlus />
          Comment
        </Button>
      ),
    })
  }

  const searchHits = debounced.length >= 2 ? search.data?.hits ?? null : null
  const exportPrompt = () => {
    if (!current) return
    if (!exportReady) {
      toast.error(threads.isError ? "Comments could not be loaded, so the export would be incomplete." : "Comments are still loading.")
      return
    }
    downloadText(`${current.id}.md`, exportMarkdown(current, allThreads))
  }

  return (
    <div className="flex h-[calc(100svh-4rem)]">
      <aside className="hidden w-72 shrink-0 border-r bg-sidebar/40 md:block">
        {index.data && (
          <PromptNav
            entries={index.data.entries}
            selected={selected}
            outlineItems={isEntry ? outlineItems : []}
            searchHits={searchHits}
            query={query}
            onQuery={setQuery}
            onSelect={select}
            onJump={jump}
          />
        )}
      </aside>
      <div ref={scrollRef} className="min-w-0 flex-1 overflow-y-auto">
        <div className="border-b p-3 md:hidden">
          <label className="sr-only" htmlFor="prompt-picker">Prompt</label>
          <select
            id="prompt-picker"
            value={selected}
            onChange={(event) => select(event.target.value)}
            className="w-full rounded-md border bg-background px-2 py-1.5 text-sm"
          >
            <option value={OVERVIEW_ID}>Overview</option>
            {(index.data?.entries ?? []).map((entry) => (
              <option key={entry.id} value={entry.id}>{entry.group}: {entry.title}</option>
            ))}
          </select>
        </div>
        {index.isError && (
          <p role="alert" className="p-8 text-sm text-destructive">Could not load the prompt list. Reload the page to try again.</p>
        )}
        {index.isLoading && <p className="p-8 text-sm text-muted-foreground">Assembling every prompt from code</p>}
        {!isEntry && index.data && <AuditOverview index={index.data} onSelect={select} />}
        {isEntry && detail.isError && (
          <p role="alert" className="p-8 text-sm text-destructive">Could not load this prompt. It may not exist in this deploy.</p>
        )}
        {isEntry && detail.isLoading && <p className="p-8 text-sm text-muted-foreground">Loading prompt</p>}
        {isEntry && current && (
          <div className="mx-auto flex max-w-[1240px] gap-10 px-6 py-8 lg:px-10">
            <article className="min-w-0 max-w-[74ch] flex-1">
              <PromptHeader
                detail={current}
                summaries={summaries}
                mode={mode}
                showResolved={showResolved}
                canComment={canComment}
                commentsAvailable={commentsAvailable}
                onMode={setMode}
                onShowResolved={setShowResolved}
                onNavigate={select}
                onJump={jump}
                exportReady={exportReady}
                onExport={exportPrompt}
              />
              {PREVIEW_IDS.has(current.id) && <div className="mt-6"><MemberPreview /></div>}
              {wide && unplaced.length > 0 && (
                <section aria-label="Comments without a visible highlight" className="mt-6 rounded-lg border p-4">
                  <h2 className="text-sm font-semibold">Comments not highlighted in this view</h2>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Their text overlaps another comment or is markdown syntax. Switch to Source to see every highlight.
                  </p>
                  <div className="mt-3 space-y-3">{unplaced.map(threadCard)}</div>
                </section>
              )}
              {unanchored.length > 0 && (
                <section aria-label="Comments on text that changed" className="mt-6 rounded-lg border border-amber-500/40 bg-amber-500/5 p-4">
                  <h2 className="text-sm font-semibold">Comments on text that is no longer in this prompt</h2>
                  <div className="mt-3 space-y-3">{unanchored.map(threadCard)}</div>
                </section>
              )}
              <div
                ref={setReader}
                className="relative mt-8"
                onMouseUp={captureSelection}
                onKeyUp={(event) => {
                  if (event.shiftKey) captureSelection()
                }}
              >
                <PromptReader
                  text={text}
                  blocks={blocks}
                  mode={mode}
                  highlights={highlights}
                  activeThread={activeThread}
                  parts={current.parts}
                  onHighlightClick={onHighlightClick}
                />
              </div>
              {!wide && pending && (
                <div className="fixed inset-x-4 bottom-4 z-20 mx-auto max-w-md">
                  {marginEntries.find((entry) => entry.id === PENDING_ID)?.node}
                </div>
              )}
              {!wide && anchored.length > 0 && (
                <section aria-label="Comments list" className="mt-10 space-y-3">
                  <h2 className="text-sm font-semibold">Comments</h2>
                  {anchored.map(threadCard)}
                </section>
              )}
            </article>
            {wide && (
              <aside aria-label="Comments" className="w-80 shrink-0" style={{ paddingTop: readerOffset }}>
                <CommentMargin
                  entries={marginEntries}
                  pinned={pending ? PENDING_ID : activeThread}
                  minHeight={reader?.offsetHeight ?? 0}
                />
              </aside>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
